/**
 * 任意テキストテンプレート画面のエラー / 警告メッセージを日本語に変換するモジュール。
 *
 * バックエンド (`lambda/control-panel-api/index.py`) の入力検証は英語の `ValueError` の文を
 * 400 の `message` としてそのまま返す。画面にそのまま出すと何を直せばよいか伝わらないため、
 * 日本語の文と原因の入力欄に変換する。原因の欄が特定できないもの
 * (認証切れ / サーバエラー / 通信断など) は画面上部のバナーに出す。
 *
 * 対応表 (`SERVER_MESSAGE_RULES`) は `index.py` の `raise ValueError(...)` と対になる。
 * 更新漏れに気づけるよう、`freeTextTemplateMessages.test.ts` が `index.py` を読んで突き合わせている。
 */
import { ApiError } from '../api/common';
import type { FreeTextTemplateWarning } from '../api/types';

import {
  MAX_FREE_TEXT_TEMPLATE_TEXT_LENGTH,
  MAX_TEMPLATE_NOTE_LENGTH,
  MAX_TEMPLATE_TITLE_LENGTH,
} from '../data/freeTextTemplates';

/** エラーの原因になった入力欄 */
export type FreeTextTemplateErrorField = 'route' | 'title' | 'text' | 'note';

/** 画面表示用に整形したエラー */
export interface FreeTextTemplateErrorInfo {
  /** 利用者向けの日本語メッセージ */
  message: string;
  /** 原因の入力欄。特定できない場合は null (バナー表示にする) */
  field: FreeTextTemplateErrorField | null;
}

/** 必須項目が空のときのメッセージ (クライアント側の検証とサーバ文言の対応表で共用する) */
export const FREE_TEXT_TEMPLATE_REQUIRED_MESSAGES = {
  route: '線区を選択してください',
  title: 'タイトルを入力してください',
  text: 'テキストを入力してください',
} as const;

/** 通信そのものに失敗したとき (fetch が TypeError を投げる) */
export const NETWORK_ERROR_MESSAGE = '通信に失敗しました。ネットワークの接続を確認してください';

/** 原因が分からない例外 */
export const UNEXPECTED_ERROR_MESSAGE = '予期しないエラーが発生しました';

interface ServerMessageRule {
  /** バックエンドの英語メッセージ全体に一致させる正規表現 */
  pattern: RegExp;
  field: FreeTextTemplateErrorField | null;
  message: (match: RegExpMatchArray) => string;
}

/** バックエンドの検証メッセージ → 日本語 + 対象欄の対応表 */
export const SERVER_MESSAGE_RULES: readonly ServerMessageRule[] = [
  {
    pattern: /^route_id is required$/,
    field: 'route',
    message: () => FREE_TEXT_TEMPLATE_REQUIRED_MESSAGES.route,
  },
  {
    pattern: /^route_id must be a string$/,
    field: 'route',
    message: () => '線区の指定が正しくありません',
  },
  {
    pattern: /^route_id .+ is not a known route$/,
    field: 'route',
    message: () => 'この線区は登録できません。一覧から選択してください',
  },
  {
    pattern: /^title is required$/,
    field: 'title',
    message: () => FREE_TEXT_TEMPLATE_REQUIRED_MESSAGES.title,
  },
  {
    pattern: /^title must be a string$/,
    field: 'title',
    message: () => 'タイトルは文字列で入力してください',
  },
  {
    pattern: /^title must be a single line$/,
    field: 'title',
    message: () => 'タイトルは改行を含めず 1 行で入力してください',
  },
  {
    pattern: /^title exceeds (\S+) characters$/,
    field: 'title',
    message: (m) => `タイトルは${m[1]}文字以内で入力してください`,
  },
  {
    pattern: /^note must be a string$/,
    field: 'note',
    message: () => '備考は文字列で入力してください',
  },
  {
    pattern: /^note exceeds (\S+) characters$/,
    field: 'note',
    message: (m) => `備考は${m[1]}文字以内で入力してください`,
  },
  {
    pattern: /^text is required$/,
    field: 'text',
    message: () => FREE_TEXT_TEMPLATE_REQUIRED_MESSAGES.text,
  },
  {
    pattern: /^text must be a string$/,
    field: 'text',
    message: () => 'テキストは文字列で入力してください',
  },
  {
    pattern: /^text exceeds (\S+) characters$/,
    field: 'text',
    message: (m) => `テキストは${m[1]}文字以内で入力してください`,
  },
  // 画面からは発生しない (template_id は一覧の値をそのまま使う) が、念のため対応表に持つ
  {
    pattern: /^template_id (is required|must be a UUID)$/,
    field: null,
    message: () => '対象のテキストの指定が正しくありません。一覧を再読み込みしてください',
  },
];

/** バックエンドの 400 メッセージを日本語 + 対象欄に変換する。対応表に無ければ null。 */
export function translateServerMessage(serverMessage: string): FreeTextTemplateErrorInfo | null {
  for (const rule of SERVER_MESSAGE_RULES) {
    const match = serverMessage.match(rule.pattern);
    if (match) return { message: rule.message(match), field: rule.field };
  }
  return null;
}

/** 日本語の文字 (ひらがな / カタカナ / 漢字) を含むか */
function containsJapanese(text: string): boolean {
  return /[\u3040-\u30ff\u3400-\u9fff]/.test(text);
}

/** HTTP ステータスとサーバのメッセージから、画面表示用のエラーを作る。 */
function describeHttpError(status: number, serverMessage: string): FreeTextTemplateErrorInfo {
  if (status === 400) {
    const translated = translateServerMessage(serverMessage);
    if (translated) return translated;
    return {
      message: containsJapanese(serverMessage)
        ? serverMessage
        : `入力内容を確認してください(詳細: ${serverMessage})`,
      field: null,
    };
  }
  if (status === 401 || status === 403) {
    return {
      message: 'ログインの有効期限が切れたか、この操作の権限がありません。再度ログインしてください',
      field: null,
    };
  }
  if (status === 404) {
    return {
      message:
        '対象のテキストが見つかりません。すでに削除された可能性があります。一覧を再読み込みしてください',
      field: null,
    };
  }
  if (status >= 500) {
    return {
      message: 'サーバでエラーが発生しました。時間をおいて、もう一度お試しください',
      field: null,
    };
  }
  return { message: `処理に失敗しました(HTTP ${status})`, field: null };
}

/**
 * API クライアントの例外を、画面表示用の日本語メッセージ + 対象欄に変換する。
 *
 * - `ApiError`: ステータス別に変換 (400 は対応表で欄まで特定する)
 * - `TypeError`: `fetch` が通信失敗時に投げるもの → 通信エラー
 * - それ以外の `Error`: 日本語ならそのまま、英語なら詳細として添える
 */
export function describeApiError(err: unknown): FreeTextTemplateErrorInfo {
  if (err instanceof ApiError) {
    return describeHttpError(err.status, err.message);
  }
  if (err instanceof TypeError) {
    return { message: NETWORK_ERROR_MESSAGE, field: null };
  }
  if (err instanceof Error) {
    return {
      message: containsJapanese(err.message)
        ? err.message
        : `${UNEXPECTED_ERROR_MESSAGE}(詳細: ${err.message})`,
      field: null,
    };
  }
  return { message: UNEXPECTED_ERROR_MESSAGE, field: null };
}

/** 保存時の警告 (保存は成功しているが確認してほしい点) を日本語にする。未知のコードはサーバの文をそのまま返す。 */
export function translateWarning(warning: FreeTextTemplateWarning): string {
  if (warning.code === 'duplicate_text') {
    return 'この線区に、同じテキストがすでに登録されています。重複していないか確認してください';
  }
  return warning.message;
}

/**
 * 登録 / 更新の完了ポップアップの本文を組み立てる。
 * 警告があれば、完了の文言の後ろに箇条書きで続けて 1 つのポップアップにまとめる。
 */
export function buildSavedNoticeMessage(
  action: '登録' | '更新',
  warnings: readonly FreeTextTemplateWarning[],
  /** 対象の名称 (コピー型の「独自テンプレート」と参照型の「配布テンプレート」を使い分ける。既定は「独自テンプレート」) */
  subject = '独自テンプレート',
): string {
  const done = `${subject}を${action}しました`;
  if (warnings.length === 0) return done;
  const items = warnings.map((warning) => `・${translateWarning(warning)}`).join('\n');
  return `${done}\n\n次の点を確認してください。\n${items}`;
}

/** テキスト欄の文字数カウンタ表示 (例: `12 / 3000`) */
export function formatTextCounter(text: string): string {
  return `${Array.from(text.trim()).length} / ${MAX_FREE_TEXT_TEMPLATE_TEXT_LENGTH}`;
}

/** タイトル欄の文字数カウンタ表示 (例: `5 / 100`) */
export function formatTitleCounter(title: string): string {
  return `${Array.from(title.trim()).length} / ${MAX_TEMPLATE_TITLE_LENGTH}`;
}

/** 備考欄の文字数カウンタ表示 (例: `5 / 1000`) */
export function formatNoteCounter(note: string): string {
  return `${Array.from(note.trim()).length} / ${MAX_TEMPLATE_NOTE_LENGTH}`;
}
